import { useEffect, useState } from "react";
import { Layout } from "@/components/layout";
import { BankDetailsNoticeControls } from "@/components/bank-details-notice-controls";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Loader2, Mail, RefreshCw, Eye, AlertTriangle } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface EmailRow {
  id: number;
  sentAt: string;
  toEmail: string;
  originalTo: string;
  fromEmail: string | null;
  fromName: string | null;
  replyTo: string | null;
  subject: string;
  reason: string;
  bodyHtml: string | null;
  bodyText: string | null;
  overridden: boolean;
  error: string | null;
}

interface EmailLogResponse {
  rows: EmailRow[];
  total: number;
  limit: number;
  offset: number;
}

const REASON_COLOURS: Record<string, string> = {
  "Remittance request": "bg-blue-100 text-blue-800 border-blue-200",
  "Bank details remittance acknowledgement": "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Bank details notice test": "bg-amber-100 text-amber-800 border-amber-200",
  "Courier price challenge": "bg-amber-100 text-amber-800 border-amber-200",
  "Password reset": "bg-violet-100 text-violet-800 border-violet-200",
  "Contract alert": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Receipt reminder": "bg-slate-100 text-slate-800 border-slate-200",
  "SMTP test": "bg-pink-100 text-pink-800 border-pink-200",
};

export default function EmailLogPage() {
  const { user, isLoading: authLoading, isSuperAdmin } = useAuth();
  const userId = user?.id;
  const [, setLocation] = useLocation();
  const [data, setData] = useState<EmailLogResponse | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterReason, setFilterReason] = useState<string>("");
  const [filterRecipient, setFilterRecipient] = useState<string>("");
  const [preview, setPreview] = useState<EmailRow | null>(null);
  const linkedId = new URLSearchParams(window.location.search).get("id");

  useEffect(() => {
    if (authLoading) return;
    if (!userId) setLocation("/login");
  }, [authLoading, userId, setLocation]);

  async function fetchLog() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (linkedId) params.set("id", linkedId);
      if (filterReason) params.set("reason", filterReason);
      if (filterRecipient.trim()) params.set("recipient", filterRecipient.trim());
      const res = await fetch(`${BASE}/api/email-log?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err: any) {
      setError(err?.message ?? "Failed to load email log");
    } finally {
      setLoading(false);
    }
  }

  async function fetchReasons() {
    try {
      const res = await fetch(`${BASE}/api/email-log/reasons`, { credentials: "include" });
      if (res.ok) setReasons(await res.json());
    } catch {
      /* non-fatal */
    }
  }

  useEffect(() => {
    if (userId) {
      fetchLog();
      fetchReasons();
    }
    // useAuth returns a fresh user object on each render. Depend on its stable
    // ID so setting loading/data cannot trigger another request indefinitely.
    // Filters are intentionally applied by the Apply/Refresh buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, linkedId]);

  if (!user) return null;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-slate-900">Email log</h1>
          </div>
          <Button onClick={fetchLog} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Every outbound email the app has tried to send. Hover the subject for a preview, or click <Eye className="inline w-3 h-3 -mt-0.5" /> for the full body. Latest 200 shown.
        </p>
        {isSuperAdmin && <BankDetailsNoticeControls isSuperAdmin={isSuperAdmin} />}

        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Reason</label>
            <select
              value={filterReason}
              onChange={(e) => setFilterReason(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[220px]"
            >
              <option value="">All reasons</option>
              {reasons.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Recipient contains</label>
            <input
              value={filterRecipient}
              onChange={(e) => setFilterRecipient(e.target.value)}
              placeholder="e.g. michelle.harding or duffells.co.uk"
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[280px]"
            />
          </div>
          <Button onClick={fetchLog} disabled={loading}>Apply</Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : data ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
              {data.total} email{data.total === 1 ? "" : "s"} match{data.total === 1 ? "es" : ""} filter
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <th className="px-3 py-2 font-medium">Sent (UTC)</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                    <th className="px-3 py-2 font-medium">To (delivered)</th>
                    <th className="px-3 py-2 font-medium">Originally for</th>
                    <th className="px-3 py-2 font-medium">From</th>
                    <th className="px-3 py-2 font-medium">Subject</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const previewText = (row.bodyText
                      ?? row.bodyHtml?.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
                      ?? "");
                    const truncatedPreview = previewText.length > 800
                      ? previewText.slice(0, 800) + "…"
                      : previewText;
                    return (
                      <tr key={row.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700 text-xs">
                          {new Date(row.sentAt).toISOString().replace("T", " ").replace(/\..+/, "")}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${REASON_COLOURS[row.reason] ?? "bg-slate-100 text-slate-700 border-slate-200"}`}>
                            {row.reason}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700 text-xs font-mono">{row.toEmail}</td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {row.overridden ? (
                            <span className="text-amber-700" title="This send was diverted by a test-override; the customer did NOT receive it.">
                              {row.originalTo} <span className="text-amber-500">⤴ test-divert</span>
                            </span>
                          ) : (
                            <span className="text-slate-500">{row.originalTo}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">
                          {row.fromName ? `${row.fromName} <${row.fromEmail ?? ""}>` : (row.fromEmail ?? "—")}
                        </td>
                        <td
                          className="px-3 py-2 text-slate-800 max-w-[360px] truncate cursor-help"
                          title={truncatedPreview || row.subject}
                        >
                          {row.subject}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.error ? (
                            <span className="inline-flex items-center gap-1 text-red-700" title={row.error}>
                              <AlertTriangle className="w-3 h-3" /> failed
                            </span>
                          ) : (
                            <span className="text-emerald-700">sent</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setPreview(row)}
                            className="text-slate-500 hover:text-slate-900"
                            title="View full content"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-slate-400 text-sm">
                        No emails match the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      {preview && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs text-slate-500">
                  {new Date(preview.sentAt).toISOString().replace("T", " ").replace(/\..+/, "")} UTC
                  <span className={`ml-2 inline-block px-2 py-0.5 rounded-md text-xs border ${REASON_COLOURS[preview.reason] ?? "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {preview.reason}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 truncate">{preview.subject}</h2>
                <div className="text-xs text-slate-600 mt-1">
                  <div><span className="text-slate-400">From:</span> {preview.fromName ? `${preview.fromName} <${preview.fromEmail ?? ""}>` : (preview.fromEmail ?? "—")}</div>
                  <div>
                    <span className="text-slate-400">To:</span> <span className="font-mono">{preview.toEmail}</span>
                    {preview.overridden && (
                      <span className="ml-2 text-amber-700">
                        (test-divert — originally for <span className="font-mono">{preview.originalTo}</span>)
                      </span>
                    )}
                  </div>
                  {preview.replyTo && (
                    <div><span className="text-slate-400">Reply-To:</span> <span className="font-mono">{preview.replyTo}</span></div>
                  )}
                  {preview.error && (
                    <div className="text-red-700 mt-1"><AlertTriangle className="inline w-3 h-3 mr-1" />Send failed: {preview.error}</div>
                  )}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto p-5 flex-1 bg-slate-50">
              {preview.bodyHtml ? (
                // Rendered inside a sandboxed iframe (no scripts, no
                // same-origin, no top-nav) rather than via
                // dangerouslySetInnerHTML so any malicious HTML that
                // might land in the log (e.g. via an attacker-supplied
                // recipient interpolated into a future template) cannot
                // exfiltrate the operator's session or execute scripts
                // in the parent app. srcdoc keeps the document strictly
                // inline so the iframe has no URL of its own.
                <iframe
                  title={`Email body #${preview.id}`}
                  sandbox=""
                  srcDoc={`<!doctype html><html><body style="margin:0;padding:16px;background:white;font-family:sans-serif;">${preview.bodyHtml}</body></html>`}
                  className="bg-white rounded-lg border border-slate-200 w-full"
                  style={{ minHeight: 400, height: "60vh" }}
                />
              ) : preview.bodyText ? (
                <pre className="bg-white rounded-lg border border-slate-200 p-4 text-sm whitespace-pre-wrap text-slate-800">{preview.bodyText}</pre>
              ) : (
                <p className="text-slate-400 italic">No body content recorded.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
